import { useState, useCallback, useMemo } from 'react';

interface UseGridFilterParams {
  initialFilters?: Record<string, any>;
  onFilter?: (filters: Record<string, any>) => void;
  debounceMs?: number;
}

export const useGridFilter = ({
  initialFilters = {},
  onFilter,
  debounceMs = 300,
}: UseGridFilterParams) => {
  const [filters, setFilters] = useState<Record<string, any>>(initialFilters);
  const [pendingFilters, setPendingFilters] = useState<Record<string, any>>({});

  // Handle filter change with optional debouncing
  const handleFilterChange = useCallback(
    (key: string, value: any) => {
      const newFilters = { ...filters, [key]: value };

      // Clean up empty values
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        delete newFilters[key];
      }

      setFilters(newFilters);
      onFilter?.(newFilters);
    },
    [filters, onFilter]
  );

  // Handle debounced filter change (for search inputs)
  const handleDebouncedFilterChange = useCallback(
    (key: string, value: any) => {
      setPendingFilters(prev => ({ ...prev, [key]: value }));

      const timeoutId = setTimeout(() => {
        handleFilterChange(key, value);
        setPendingFilters(prev => {
          const { [key]: _removed, ...rest } = prev;
          return rest;
        });
      }, debounceMs);

      return () => clearTimeout(timeoutId);
    },
    [handleFilterChange, debounceMs]
  );

  // Reset all filters
  const resetFilters = useCallback(() => {
    setFilters({});
    setPendingFilters({});
    onFilter?.({});
  }, [onFilter]);

  // Reset specific filter
  const resetFilter = useCallback(
    (key: string) => {
      const newFilters = { ...filters };
      delete newFilters[key];
      setFilters(newFilters);
      onFilter?.(newFilters);
    },
    [filters, onFilter]
  );

  // Set multiple filters at once
  const setMultipleFilters = useCallback(
    (newFilters: Record<string, any>) => {
      setFilters(newFilters);
      onFilter?.(newFilters);
    },
    [onFilter]
  );

  // Check if a specific filter is active
  const isFilterActive = useCallback(
    (key: string): boolean => {
      const value = filters[key];
      return (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        (!Array.isArray(value) || value.length > 0)
      );
    },
    [filters]
  );

  // Get filter value
  const getFilterValue = useCallback(
    (key: string) => {
      return filters[key];
    },
    [filters]
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(
      value =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        (!Array.isArray(value) || value.length > 0)
    );
  }, [filters]);

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(
      value =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        (!Array.isArray(value) || value.length > 0)
    ).length;
  }, [filters]);

  // Filter data utility function
  const filterData = useCallback(
    (data: any[], customFilters?: Record<string, any>) => {
      const activeFilters = customFilters || filters;

      if (!Object.keys(activeFilters).length) return data;

      return data.filter(row => {
        return Object.entries(activeFilters).every(([key, filterValue]) => {
          if (!filterValue || filterValue === '') return true;

          const cellValue = row[key];
          if (cellValue == null) return false;

          // Handle different filter types
          if (Array.isArray(filterValue)) {
            // Multi-select filter
            if (Array.isArray(cellValue)) {
              return filterValue.some(fv => cellValue.includes(fv));
            }
            return filterValue.includes(cellValue);
          }

          if (typeof filterValue === 'string') {
            // Text search filter
            if (filterValue.startsWith('^') && filterValue.endsWith('$')) {
              // Exact match
              const exactValue = filterValue.slice(1, -1);
              return cellValue.toString().toLowerCase() === exactValue.toLowerCase();
            } else if (filterValue.startsWith('^')) {
              // Starts with
              const startsValue = filterValue.slice(1);
              return cellValue.toString().toLowerCase().startsWith(startsValue.toLowerCase());
            } else if (filterValue.endsWith('$')) {
              // Ends with
              const endsValue = filterValue.slice(0, -1);
              return cellValue.toString().toLowerCase().endsWith(endsValue.toLowerCase());
            } else {
              // Contains
              return cellValue.toString().toLowerCase().includes(filterValue.toLowerCase());
            }
          }

          if (typeof filterValue === 'number') {
            // Numeric filter
            return Number(cellValue) === filterValue;
          }

          if (typeof filterValue === 'boolean') {
            // Boolean filter
            return Boolean(cellValue) === filterValue;
          }

          if (filterValue instanceof Date) {
            // Date filter
            const cellDate = new Date(cellValue);
            return cellDate.toDateString() === filterValue.toDateString();
          }

          if (typeof filterValue === 'object' && filterValue !== null) {
            // Range filter
            if ('min' in filterValue || 'max' in filterValue) {
              const numericValue = Number(cellValue);
              if (isNaN(numericValue)) return false;

              if (filterValue.min !== undefined && numericValue < filterValue.min) return false;
              if (filterValue.max !== undefined && numericValue > filterValue.max) return false;
              return true;
            }

            // Date range filter
            if ('from' in filterValue || 'to' in filterValue) {
              const cellDate = new Date(cellValue);
              if (isNaN(cellDate.getTime())) return false;

              if (filterValue.from && cellDate < new Date(filterValue.from)) return false;
              if (filterValue.to && cellDate > new Date(filterValue.to)) return false;
              return true;
            }
          }

          // Default equality check
          return cellValue === filterValue;
        });
      });
    },
    [filters]
  );

  // Generate filter summary for accessibility/display
  const getFilterSummary = useCallback(() => {
    const activeFilters = Object.entries(filters).filter(
      ([_, value]) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        (!Array.isArray(value) || value.length > 0)
    );

    if (activeFilters.length === 0) return 'No filters applied';
    if (activeFilters.length === 1) return '1 filter applied';
    return `${activeFilters.length} filters applied`;
  }, [filters]);

  // Get readable filter descriptions
  const getFilterDescriptions = useCallback(() => {
    return Object.entries(filters)
      .filter(
        ([_, value]) =>
          value !== null &&
          value !== undefined &&
          value !== '' &&
          (!Array.isArray(value) || value.length > 0)
      )
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}: ${value.length} selected`;
        }
        if (typeof value === 'string' && value.length > 20) {
          return `${key}: ${value.substring(0, 20)}...`;
        }
        return `${key}: ${value}`;
      });
  }, [filters]);

  return {
    filters,
    pendingFilters,
    handleFilterChange,
    handleDebouncedFilterChange,
    resetFilters,
    resetFilter,
    setMultipleFilters,
    isFilterActive,
    getFilterValue,
    hasActiveFilters,
    activeFilterCount,
    filterData,
    getFilterSummary,
    getFilterDescriptions,

    // For external state updates
    setFilters: useCallback(
      (newFilters: Record<string, any>) => {
        setFilters(newFilters);
        onFilter?.(newFilters);
      },
      [onFilter]
    ),
  };
};
